import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, mediaUrl } from "../../Utils";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../Context/AuthContext";

interface FormValues {
    branch_id: string;
    sale_date: string;
    sale_price: string;
    buyer_name: string;
    buyer_contact: string;
    odometer_reading?: string;
    notes?: string;
    payment_document?: FileList;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function EditSoldVehicle() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = location.state || {};
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicleInfo, setVehicleInfo] = useState<any>(null);
    const [docPreview, setDocPreview] = useState<string | null>(null);
    const {
        control,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm<FormValues>();
    const { hasPermission } = useAuth();

    useEffect(() => {
        if (!id) {
            navigate(`${process.env.PUBLIC_URL}/sold_vehicle`);
            return;
        }

        const fetchBranches = async () => {
            try {
                const data: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(data);
            } catch (error) {
                console.error(error);
            }
        };
        fetchBranches();
    }, [id, navigate]);

    useEffect(() => {
        if (id) {
            const fetchSaleData = async () => {
                try {
                    const response: any = await apiPost("vehicle_sales/getall", { search: "", page: 1, per_page: 1000 });
                    const sale = response.data?.find((s: any) => s.id === id);
                    if (sale) {
                        reset({
                            branch_id: sale.branch_id || "",
                            sale_date: sale.sale_date || "",
                            sale_price: sale.sale_price || "",
                            buyer_name: sale.buyer_name || "",
                            buyer_contact: sale.buyer_contact || "",
                            odometer_reading: sale.odometer_reading || "",
                            notes: sale.notes || "",
                        });
                        setVehicleInfo(sale.vehicle);
                        if (sale.payment_document) {
                            setDocPreview(`${mediaUrl}${sale.payment_document}`);
                        }
                    }
                } catch (error) {
                    console.error(error);
                }
            };
            fetchSaleData();
        }
    }, [id, reset]);

    const onSubmit = async (data: FormValues) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('id', id);

            // Append all text fields
            Object.keys(data).forEach(key => {
                if (key !== 'payment_document') {
                    const value = (data as any)[key];
                    if (value instanceof Date) {
                        formData.append(key, formatDate(value));
                    } else {
                        formData.append(key, value || '');
                    }
                }
            });

            // Append File if new one selected
            if (data.payment_document && data.payment_document.length > 0 && data.payment_document[0] instanceof File) {
                formData.append('payment_document', data.payment_document[0]);
            }

            await apiPost("vehicle_sales/update", formData);
            toast.success("Sale details updated!", { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/sold_vehicle`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error updating sale");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Edit Sale" parent="Vehicle Sales" title="Edit Sold Vehicle" />
            {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '70vh' }}><Spinner color="primary" /></div> : (
                <Container fluid>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <H5>Sale Details</H5>
                                <hr />
                                <Row className="g-3">

                                    <Col md={6}>
                                        <Label>Branch <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="branch_id"
                                            control={control}
                                            rules={{ required: "Branch is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.branch_id} disabled>
                                                        <option value="">Select Branch</option>
                                                        {branches.map((b: any) => (
                                                            <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.branch_id?.message as string}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Label>Vehicle</Label>
                                        <Input
                                            type="text"
                                            value={vehicleInfo ? `${vehicleInfo.registration_number} - ${vehicleInfo.brand} ${vehicleInfo.model}` : ''}
                                            disabled
                                            readOnly
                                        />
                                    </Col>

                                    <Col md={4}>
                                        <Label>Sale Date <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="sale_date"
                                            control={control}
                                            rules={{ required: "Sale Date is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.sale_date ? "is-invalid" : ""}`}
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => {
                                                            const d = date ? formatDate(date) : "";
                                                            field.onChange(d);
                                                        }}
                                                        dateFormat="dd/MM/yyyy"
                                                        placeholderText="DD/MM/YYYY"
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                    />
                                                    <FormFeedback className="d-block">{errors.sale_date?.message as string}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={4}>
                                        <Label>Sale Price <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="sale_price"
                                            control={control}
                                            rules={{ required: "Sale Price is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        {...field}
                                                        invalid={!!errors.sale_price}
                                                        autoComplete="off"
                                                    />
                                                    <FormFeedback>{errors.sale_price?.message as string}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={4}>
                                        <Label>Odometer Reading</Label>
                                        <Controller
                                            name="odometer_reading"
                                            control={control}
                                            render={({ field }) => (
                                                <Input {...field} value={field.value || ''} autoComplete="off" />
                                            )}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <Label>Buyer Name <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="buyer_name"
                                            control={control}
                                            rules={{ required: "Buyer Name is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} invalid={!!errors.buyer_name} autoComplete="off" />
                                                    <FormFeedback>{errors.buyer_name?.message as string}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <Label>Buyer Contact <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="buyer_contact"
                                            control={control}
                                            rules={{ required: "Buyer Contact is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} invalid={!!errors.buyer_contact} autoComplete="off" />
                                                    <FormFeedback>{errors.buyer_contact?.message as string}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={12}>
                                        <Label>Notes</Label>
                                        <Controller
                                            name="notes"
                                            control={control}
                                            render={({ field }) => (
                                                <Input type="textarea" {...field} value={field.value || ''} autoComplete="off" />
                                            )}
                                        />
                                    </Col>
                                    <Col md={12}>
                                        <Label>Payment Document <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="payment_document"
                                            control={control}
                                            rules={{ required: !docPreview && "Payment Document is required" }}
                                            render={({ field: { value, onChange, ...field } }) => (
                                                <>
                                                    <Input
                                                        type="file"
                                                        {...field}
                                                        accept="image/*,.pdf"
                                                        onChange={(e) => {
                                                            if (e.target.files && e.target.files.length > 0) {
                                                                const file = e.target.files[0];
                                                                if (file.size > 2 * 1024 * 1024) {
                                                                    toast.error("File size must be less than 2MB");
                                                                    e.target.value = ""; // Clear the input
                                                                    onChange(null); // Clear form state
                                                                    return;
                                                                }
                                                            }
                                                            onChange(e.target.files);
                                                        }}
                                                        invalid={!!errors.payment_document}
                                                    />
                                                    <FormFeedback>{errors.payment_document?.message}</FormFeedback>
                                                    {docPreview && !(value instanceof FileList && value.length > 0) && (
                                                        <a href={docPreview} target="_blank" rel="noreferrer" className="d-block mt-2">View Current Document</a>
                                                    )}
                                                    {value instanceof FileList && value.length > 0 && (
                                                        <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2">View New Document</a>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Col>
                                </Row>

                                {hasPermission('vehicle-sales.edit') && (
                                    <div className="mt-4">
                                        <Btn color="primary" disabled={loading} type="submit">
                                            {loading ? <>Updating <Spinner size="sm" /></> : "Update"}
                                        </Btn>
                                    </div>
                                )}
                            </Form>
                        </CardBody>
                    </Card>
                </Container>
            )}
            <ToastContainer />
        </div>
    );
}
